import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Hub } from "aws-amplify/utils";
import {
    confirmResetPassword,
    confirmSignUp,
    fetchAuthSession,
    fetchUserAttributes,
    getCurrentUser,
    resendSignUpCode,
    resetPassword,
    signIn,
    signInWithRedirect,
    signOut,
    signUp,
    updatePassword,
    updateUserAttributes,
} from "aws-amplify/auth";
import { requireConnection } from "../utils/connection";
import * as analytics from "../utils/analytics";
import { isDesktopLocalProfile, LOCAL_PROFILE_ID } from "../utils/desktopProfile";
import { completeTwitterSignIn as completeTwitterSignInRequest, startTwitterLink, startTwitterSignIn } from "../utils/twitterAuth";
import { classifyOAuthError, hasPendingOAuthReturn, readOAuthRedirectError, SocialSignInErrorCode } from "../utils/socialSignIn";

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export interface AuthUser {
    username: string;
    email?: string;
    displayName: string;
    groups: string[];
    isAdmin: boolean;
    identityId?: string;
}

interface Credentials {
    email: string;
    password: string;
}

interface AuthContextValue {
    status: AuthStatus;
    isSigningOut: boolean;
    user: AuthUser | null;
    getIdToken: () => Promise<string>;
    signIn: (credentials: Credentials) => Promise<void>;
    signUp: (details: Credentials & { displayName: string }) => Promise<unknown>;
    confirmSignUp: (details: { email: string; code: string }) => Promise<void>;
    resendSignUpCode: (details: { email: string }) => Promise<void>;
    signOut: () => Promise<void>;
    requestPasswordReset: (details: { email: string }) => Promise<void>;
    confirmPasswordReset: (details: { email: string; code: string; newPassword: string }) => Promise<void>;
    changePassword: (details: { oldPassword: string; newPassword: string }) => Promise<void>;
    updateDisplayName: (details: { displayName: string }) => Promise<void>;
    signInWithGoogle: () => Promise<void>;
    signInWithFacebook: () => Promise<void>;
    /**
     * Why the last Google/Facebook round-trip failed, as a short code to translate (never the
     * provider's own text). Set when the browser comes back from the hosted UI with an error and
     * consumed by SocialSignInErrorRedirect, which shows it on the login page.
     */
    oauthError: SocialSignInErrorCode | null;
    clearOauthError: () => void;
    /** `language` ("en" | "pt") is remembered across the round-trip through X so the result page matches it. */
    signInWithTwitter: (language?: string) => void;
    connectTwitter: (language?: string) => Promise<void>;
    completeTwitterSignIn: (username: string, ticket: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function isNamedError(error: unknown, name: string): boolean {
    return error instanceof Error && error.name === name;
}

// Leaves for the Cognito hosted UI. Shared by every native provider (Google, Facebook).
async function startHostedUiSignIn(input: Parameters<typeof signInWithRedirect>[0]): Promise<void> {
    try {
        await signInWithRedirect(input);
    } catch (err: unknown) {
        if (!isNamedError(err, "UserAlreadyAuthenticatedException")) throw err;
        // Amplify's local session state is stale/inconsistent with what
        // our own session checks see (e.g. leftover from an interrupted
        // sign-in) - clear it locally and retry once.
        try {
            await signOut();
        } catch {
            // best-effort local cleanup before retrying
        }
        await signInWithRedirect(input);
    }
}

async function loadCurrentUser() {
    const { username } = await getCurrentUser();
    // A verified, still locally recognised session can open its own device
    // workspace offline. This metadata grants no AWS credentials/admin role.
    if (navigator.onLine === false) {
        try {
            const cached = JSON.parse(localStorage.getItem("nihongotech.authWorkspace.v1") || "null");
            if (cached?.username === username && typeof cached.identityId === "string") {
                const groups = Array.isArray(cached.groups) ? cached.groups.filter((group: unknown): group is string => typeof group === "string") : [];
                return { username, identityId: cached.identityId, displayName: typeof cached.displayName === "string" ? cached.displayName : "", groups, isAdmin: groups.includes("admin") };
            }
        } catch { /* a missing workspace requires an online sign-in */ }
        throw new Error("Open this account online before studying offline");
    }
    const [attributes, session] = await Promise.all([
        fetchUserAttributes(),
        fetchAuthSession(),
    ]);

    const rawGroups = session.tokens?.accessToken?.payload?.["cognito:groups"];
    const groups = Array.isArray(rawGroups) ? rawGroups.filter((group): group is string => typeof group === "string") : [];

    const current = {
        username,
        email: attributes.email,
        displayName: attributes.preferred_username || "",
        groups,
        isAdmin: groups.includes("admin"),
        // The DynamoDB row-level-security policy keys off the Identity Pool's
        // identityId (via the cognito-identity.amazonaws.com:sub condition key),
        // which is NOT the same value as the User Pool's `sub` attribute.
        identityId: session.identityId,
    };
    try { localStorage.setItem("nihongotech.authWorkspace.v1", JSON.stringify({ username, identityId: current.identityId, displayName: current.displayName, groups: current.groups })); }
    catch { /* device workspace metadata is optional; cloud auth still works */ }
    return current;
}

function CloudAuthProvider({ children }: { children: ReactNode }) {
    const [status, setStatus] = useState<AuthStatus>("loading");
    const [user, setUser] = useState<AuthUser | null>(null);
    const [isSigningOut, setIsSigningOut] = useState(false);
    const signOutPending = useRef<Promise<void> | null>(null);
    // completeTwitterSignIn's confirmSignIn() fires the exact same Amplify
    // "signedIn" Hub event an email/password sign-in does (unlike Google,
    // which fires the distinguishable "signInWithRedirect" event) - this is
    // the only way the listener below can label the right analytics method
    // instead of miscounting every X sign-in as "email".
    // "facebook" is set from Amplify's customOAuthState event (the custom state
    // signInWithFacebook passes) so its redirect sign-in isn't counted as Google.
    const pendingAuthMethod = useRef<"twitter" | "facebook" | null>(null);
    const [oauthError, setOauthError] = useState<SocialSignInErrorCode | null>(null);
    const clearOauthError = useCallback(() => setOauthError(null), []);
    // True once the failure was read from the URL below, so the Hub event Amplify
    // raises for that same failure (if it is not missed) isn't handled twice.
    const urlOauthErrorSeen = useRef(false);

    const getIdToken = useCallback(async () => {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        if (!token) throw new Error("No authenticated ID token is available.");
        return token;
    }, []);

    const refreshUser = useCallback(async () => {
        try {
            const current = await loadCurrentUser();
            setUser(current);
            setStatus("authenticated");
            return current;
        } catch {
            setUser(null);
            setStatus("unauthenticated");
            return null;
        }
    }, []);

    // Cognito's local session can briefly lag both the initial page load and a
    // fresh sign-in; retry silently (without flipping status to
    // "unauthenticated" in between) so the header doesn't flash logged-out
    // buttons before settling into the signed-in state.
    const refreshUserWithRetry = useCallback(async () => {
        for (let attempt = 0; attempt < 4; attempt += 1) {
            try {
                const current = await loadCurrentUser();
                setUser(current);
                setStatus("authenticated");
                return current;
            } catch {
                if (attempt < 3) await new Promise((resolve) => setTimeout(resolve, 400));
            }
        }
        setUser(null);
        setStatus("unauthenticated");
        return null;
    }, []);

    // A failed Google/Facebook round-trip lands back on "/" with the reason in the
    // query string (see utils/socialSignIn.ts for why the URL, not Amplify's Hub
    // event, is the record to read). Only the code is kept; SocialSignInErrorRedirect
    // takes the person to the login page to see it.
    useEffect(() => {
        const failure = readOAuthRedirectError(window.location.pathname, window.location.search);
        if (failure) {
            urlOauthErrorSeen.current = true;
            setOauthError(classifyOAuthError(failure));
        }
    }, []);

    useEffect(() => {
        refreshUserWithRetry();

        const unsubscribe = Hub.listen("auth", ({ payload }) => {
            if (payload.event === "customOAuthState") {
                if (payload.data === "facebook") pendingAuthMethod.current = "facebook";
            } else if (payload.event === "signInWithRedirect_failure") {
                // Failures that carry no ?error= in the URL: the code exchange was
                // rejected (invalid_grant), the state didn't validate, or the person
                // came back from the provider with the Back button (a cancel).
                // Only when this tab really started a sign-in a moment ago: Amplify also
                // fires this on an unrelated page load when an abandoned attempt left its
                // "in progress" flag in localStorage, and that visitor must not be sent to /login.
                if (!urlOauthErrorSeen.current && hasPendingOAuthReturn()) {
                    const description = payload.data?.error?.message ?? "";
                    setOauthError((current) => current ?? classifyOAuthError({ error: "", description }));
                }
                if (pendingAuthMethod.current === "facebook") pendingAuthMethod.current = null;
            } else if (payload.event === "signedIn" || payload.event === "signInWithRedirect") {
                const method =
                    payload.event === "signInWithRedirect"
                        ? pendingAuthMethod.current === "facebook"
                            ? "facebook"
                            : "google"
                        : pendingAuthMethod.current === "twitter"
                            ? "twitter"
                            : "email";
                pendingAuthMethod.current = null;
                refreshUserWithRetry().then((current) => {
                    if (current?.identityId) {
                        analytics.identify(current.identityId);
                        analytics.track("login_completed", { method });
                    }
                });
            } else if (payload.event === "signedOut") {
                try { localStorage.removeItem("nihongotech.authWorkspace.v1"); } catch { /* unavailable */ }
                setUser(null);
                setStatus("unauthenticated");
                analytics.reset();
            }
        });

        return unsubscribe;
    }, [refreshUserWithRetry]);

    const value: AuthContextValue = {
        status,
        isSigningOut,
        user,

        getIdToken,

        oauthError,
        clearOauthError,

        async signIn({ email, password }) {
            requireConnection();
            let result;
            try {
                result = await signIn({ username: email, password });
            } catch (err: unknown) {
                if (!isNamedError(err, "UserAlreadyAuthenticatedException")) throw err;
                // Amplify's local session state is stale/inconsistent with what
                // our own session checks see - clear it locally and retry once.
                try {
                    await signOut();
                } catch {
                    // best-effort local cleanup before retrying
                }
                result = await signIn({ username: email, password });
            }
            if (!result.isSignedIn) {
                throw new Error(`Sign-in did not complete (next step: ${result.nextStep?.signInStep}).`);
            }
        },

        async signUp({ email, password, displayName }) {
            requireConnection();
            return signUp({
                username: email,
                password,
                options: { userAttributes: { email, preferred_username: displayName } },
            });
        },

        async confirmSignUp({ email, code }) {
            requireConnection();
            await confirmSignUp({ username: email, confirmationCode: code });
            analytics.track("signup_completed", { method: "email" });
        },

        async resendSignUpCode({ email }) {
            requireConnection();
            await resendSignUpCode({ username: email });
        },

        async signOut() {
            if (signOutPending.current) return signOutPending.current;
            setIsSigningOut(true);
            try { localStorage.removeItem("nihongotech.authWorkspace.v1"); } catch { /* unavailable */ }
            const pending = Promise.resolve().then(() => signOut());
            signOutPending.current = pending;
            try {
                await pending;
            } finally {
                signOutPending.current = null;
                setIsSigningOut(false);
            }
        },

        async requestPasswordReset({ email }) {
            requireConnection();
            await resetPassword({ username: email });
        },

        async confirmPasswordReset({ email, code, newPassword }) {
            requireConnection();
            await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
        },

        async changePassword({ oldPassword, newPassword }) {
            requireConnection();
            await updatePassword({ oldPassword, newPassword });
        },

        async updateDisplayName({ displayName }) {
            requireConnection();
            await updateUserAttributes({ userAttributes: { preferred_username: displayName } });
            await refreshUser();
        },

        async signInWithGoogle() {
            requireConnection();
            await startHostedUiSignIn({ provider: "Google" });
        },

        // Facebook is a native Cognito identity provider exactly like Google, so
        // it goes through the same hosted-UI redirect (identity_provider=Facebook).
        // customState only labels the analytics event on return (see the Hub
        // listener); it is not used for any security decision.
        async signInWithFacebook() {
            requireConnection();
            await startHostedUiSignIn({ provider: "Facebook", customState: "facebook" });
        },

        // Not an Amplify/Cognito-hosted-UI redirect like Google above - X
        // isn't a natively-supported Cognito identity provider (see
        // infra/lib/kanalearn-auth-stack.ts and the engineering report), so
        // this is a plain top-level navigation straight to this app's own
        // backend, which performs the whole OAuth handshake with X itself.
        signInWithTwitter(language) {
            requireConnection();
            startTwitterSignIn(language);
        },

        // "Connect X" from an already signed-in Profile page - links X to
        // the CALLER's own account rather than resolving/creating a
        // separate one. Needs the caller's own ID token (sent as a header,
        // not in the URL - see startTwitterLink), unlike signInWithTwitter
        // above which has no session yet.
        async connectTwitter(language) {
            requireConnection();
            const idToken = await getIdToken();
            await startTwitterLink(idToken, language);
        },

        // Lands here after X redirects back through this app's backend
        // with a one-time ticket (see TwitterAuthFinishPage) - answers the
        // Cognito CUSTOM_AUTH challenge to get a real session, then the
        // existing Hub "signedIn" listener above picks it up exactly like
        // any other sign-in.
        async completeTwitterSignIn(username, ticket) {
            pendingAuthMethod.current = "twitter";
            try {
                await completeTwitterSignInRequest(username, ticket);
            } catch (err) {
                pendingAuthMethod.current = null;
                throw err;
            }
        },
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Offline desktop build only (see utils/desktopProfile.ts): a fixed, signed-in-equivalent local
// profile. It never calls Cognito/Amplify, so there is no token to refresh or expire, and it grants
// nothing: no groups (Free plan), never an admin, and no getIdToken for any cloud API.
const accountsUnavailable = async (): Promise<never> => { throw new Error("Accounts are not available in the offline desktop app."); };
const LOCAL_PROFILE_AUTH: AuthContextValue = {
    status: "authenticated",
    isSigningOut: false,
    user: { username: "local-profile", displayName: "Local profile", groups: [], isAdmin: false, identityId: LOCAL_PROFILE_ID },
    getIdToken: accountsUnavailable,
    signIn: accountsUnavailable,
    signUp: accountsUnavailable,
    confirmSignUp: accountsUnavailable,
    resendSignUpCode: accountsUnavailable,
    signOut: async () => undefined,
    requestPasswordReset: accountsUnavailable,
    confirmPasswordReset: accountsUnavailable,
    changePassword: accountsUnavailable,
    updateDisplayName: accountsUnavailable,
    signInWithGoogle: accountsUnavailable,
    signInWithFacebook: accountsUnavailable,
    oauthError: null,
    clearOauthError: () => undefined,
    signInWithTwitter: () => { throw new Error("Accounts are not available in the offline desktop app."); },
    connectTwitter: accountsUnavailable,
    completeTwitterSignIn: accountsUnavailable,
};

function LocalProfileAuthProvider({ children }: { children: ReactNode }) {
    return <AuthContext.Provider value={LOCAL_PROFILE_AUTH}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
    return isDesktopLocalProfile
        ? <LocalProfileAuthProvider>{children}</LocalProfileAuthProvider>
        : <CloudAuthProvider>{children}</CloudAuthProvider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}
